import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useAuth } from "./auth"
import { tk } from "./i18n"
import { sdk } from "./medusa"

/**
 * What each person does at the shop (owners, 16 Oct 2026: "prioritise what she needs and keep
 * everything else secondary … same for Giwmes who is the delivery person, customise their Today and
 * bar"). Set by anyone in Settings → Team, kept on the server (backend workflows/hq/set-team-role.ts).
 * A role chooses the person's Today and gives them a starting bar and starred sections; nothing is
 * locked, every section stays under More, and the person can still change their own bar.
 */
export type Role = "owner" | "custom" | "courier"
export type RoleEntry = { role: Role; at: number; by: string | null }

export const ROLES: Role[] = ["owner", "custom", "courier"]
export const ROLE_LABEL: Record<Role, string> = { owner: tk("Owner"), custom: tk("Custom orders and suppliers"), courier: tk("Deliveries (courier)") }
export const ROLE_HINT: Record<Role, string> = {
  owner: tk("Everything: orders, numbers, stock and settings."),
  custom: tk("Today shows the custom orders to move, the orders being filled, the saves list and the finder."),
  courier: tk("Today shows the day's route, what to hand over and the cash collected."),
}

/** The bar a role starts with (between Today and More). */
export const ROLE_TABS: Record<Role, string[]> = {
  owner: ["/orders", "/custom", "/stock"],
  custom: ["/custom", "/finder", "/parcels", "/payments"],
  courier: ["/booking", "/courier", "/orders", "/custom"],
}

/** Sections a role finds starred (added to the person's own stars, never replacing them). */
export const ROLE_FAVOURITES: Record<Role, string[]> = {
  owner: [],
  custom: ["/suppliers", "/replies", "/messages"],
  courier: ["/parcels"],
}

type Roles = { roles: Record<string, RoleEntry> }

/** The team's roles, as one query: main.tsx asks for it as HQ starts, beside the sign-in (Today waits for it). */
export const rolesQuery = { queryKey: ["hq", "team", "roles"], queryFn: () => sdk.client.fetch<Roles>("/admin/hq/settings/roles"), staleTime: 5 * 60_000 }

export function useRoles() {
  const queryClient = useQueryClient()
  const roles = useQuery(rolesQuery)
  const set = useMutation({
    mutationFn: (body: { user_id: string; role: Role }) => sdk.client.fetch<Roles>("/admin/hq/settings/roles", { method: "POST", body }),
    onSuccess: (answer) => queryClient.setQueryData(["hq", "team", "roles"], answer),
  })
  return { roles: roles.data?.roles ?? {}, loading: roles.isLoading, failed: roles.isError, set }
}

/** The signed-in person's role. Anyone without one is an owner; `entry` is null until one is set. */
export function useMyRole(): { role: Role; entry: RoleEntry | null; loading: boolean } {
  const { user } = useAuth()
  const { roles, loading } = useRoles()
  const entry = user ? roles[user.id] ?? null : null
  return { role: entry?.role ?? "owner", entry, loading }
}
